import React, { useState } from "react";
import { marketApi } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, X, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";

export default function Watchlist({ watchlist, selected, onSelect, onChanged }) {
  const [symbol, setSymbol] = useState("");

  const add = async () => {
    const s = symbol.trim().toUpperCase();
    if (!s) return;
    try {
      await marketApi.addSymbol(s);
      setSymbol("");
      onChanged?.();
      toast.success(`${s} added to watchlist`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    }
  };

  const remove = async (s) => {
    await marketApi.removeSymbol(s);
    onChanged?.();
  };

  return (
    <div className="aq-card p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Watchlist</div>
          <div className="font-display text-lg font-bold mt-0.5">Live tape</div>
        </div>
        <div className="font-mono text-[10px] text-slate-500">{watchlist.symbols?.length || 0} symbols</div>
      </div>
      <div className="flex gap-2 mb-3">
        <Input
          data-testid={TID.addSymbolInput}
          value={symbol}
          onChange={(e) => setSymbol(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Add symbol (e.g. AMD)"
          className="bg-[#0D0E14] border-white/10 h-9 font-mono text-sm placeholder:text-slate-600"
        />
        <Button data-testid={TID.addSymbolBtn} onClick={add} className="h-9 px-3 bg-white/10 hover:bg-white/20 rounded-md">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <div className="space-y-1.5 max-h-[420px] overflow-y-auto aq-scroll pr-1">
        {(watchlist.quotes || []).map((q) => {
          const up = q.change >= 0;
          const active = selected === q.symbol;
          return (
            <div
              key={q.symbol}
              data-testid={TID.watchlistItem(q.symbol)}
              onClick={() => onSelect(q.symbol)}
              className={`group px-3 py-2.5 rounded-lg cursor-pointer transition flex items-center justify-between border ${active ? "bg-white/5 border-emerald-500/40" : "border-transparent hover:bg-white/5"}`}
            >
              <div className="min-w-0">
                <div className="font-display font-bold text-sm truncate">{q.symbol}</div>
                <div className={`font-mono text-[11px] ${up ? "text-emerald-300" : "text-rose-300"} flex items-center gap-1`}>
                  {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {up ? "+" : ""}{q.change_percent}%
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <div className="font-mono font-semibold text-sm">${q.price}</div>
                  <div className={`font-mono text-[10px] ${up ? "text-emerald-300/80" : "text-rose-300/80"}`}>{up ? "+" : ""}{q.change}</div>
                </div>
                <button
                  data-testid={TID.removeSymbolBtn(q.symbol)}
                  onClick={(e) => { e.stopPropagation(); remove(q.symbol); }}
                  className="opacity-0 group-hover:opacity-100 transition p-1 rounded hover:bg-white/10"
                >
                  <X className="w-3.5 h-3.5 text-slate-400" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
